import { Receipt, TrendingDown, TrendingUp } from 'lucide-react';
import { BILLING_LEDGER, BILLING_STATUS } from './mockCustomerPortalData';

const P = {
  silver: '#F1F5F9',
  white: '#FFFFFF',
  grid: '#E2E8F0',
  blue: '#3B82F6',
};

function StatusPill({ status }) {
  const config = BILLING_STATUS[status];
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${config.className}`}>
      {config.label}
    </span>
  );
}

export default function BillingLedger({ ledger = BILLING_LEDGER }) {
  const outstanding = ledger
    .filter((r) => r.status === 'outstanding')
    .reduce((s, r) => s + r.amountAed, 0);
  const paid = ledger
    .filter((r) => r.status === 'paid')
    .reduce((s, r) => s + r.amountAed, 0);
  const credits = ledger
    .filter((r) => r.status === 'credit')
    .reduce((s, r) => s + r.amountAed, 0);

  return (
    <section
      className="overflow-hidden rounded-2xl border shadow-sm"
      style={{ backgroundColor: P.white, borderColor: P.grid, borderWidth: 1, borderStyle: 'solid' }}
    >
      <div
        className="border-b px-5 py-4"
        style={{ borderColor: P.grid, borderBottomWidth: 1, borderBottomStyle: 'solid', backgroundColor: P.silver }}
      >
        <div className="flex items-center gap-2">
          <Receipt className="h-5 w-5" style={{ color: P.blue }} strokeWidth={1.75} />
          <div>
            <h3 className="text-base font-bold text-black">
              Financial Statement &amp; Billing Ledger
            </h3>
            <p className="text-xs text-black/70">Invoices · Statements · Credits</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-px" style={{ backgroundColor: P.grid }}>
        {[
          { label: 'Outstanding', value: outstanding, icon: TrendingDown, iconColor: '#DC2626' },
          { label: 'Paid', value: paid, icon: TrendingUp, iconColor: '#059669' },
          { label: 'Credits', value: credits, icon: null, iconColor: P.blue },
        ].map((chip) => (
          <div key={chip.label} className="px-4 py-3 text-center" style={{ backgroundColor: P.white }}>
            <p className="flex items-center justify-center gap-1 text-[10px] font-bold uppercase text-black/60">
              {chip.icon ? <chip.icon className="h-3 w-3" style={{ color: chip.iconColor }} /> : null}
              {chip.label}
            </p>
            <p className="font-mono text-lg font-bold tabular-nums text-black">
              {chip.value.toLocaleString()} <span className="text-xs font-semibold">AED</span>
            </p>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr
              className="border-b text-[10px] uppercase tracking-wider text-black/60"
              style={{ backgroundColor: P.silver, borderColor: P.grid }}
            >
              <th className="px-5 py-3 font-semibold">Reference</th>
              <th className="px-5 py-3 font-semibold">Description</th>
              <th className="px-5 py-3 font-semibold">Amount</th>
              <th className="px-5 py-3 font-semibold">Due Date</th>
              <th className="px-5 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: P.grid }}>
            {ledger.map((row) => (
              <tr key={row.id} className="transition hover:bg-[#F8FAFC]">
                <td className="px-5 py-3 font-mono text-xs font-bold text-black">{row.id}</td>
                <td className="px-5 py-3 text-black/80">{row.description}</td>
                <td className="px-5 py-3 font-mono font-bold tabular-nums text-black">
                  {row.amountAed.toLocaleString()} AED
                </td>
                <td className="px-5 py-3 text-xs text-black/70">{row.dueDate}</td>
                <td className="px-5 py-3">
                  <StatusPill status={row.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
